/**
 * Einstellungen des Cockpits: externer Umsatz je Monat und die Zeitansaetze
 * der Auslastung. Speicherform unveraendert — siehe kpiConfig.ts.
 */
import React, { useState } from 'react';
import { ChevronRight, Clock, Loader2, Save } from 'lucide-react';
import { Button } from '../Button';
import { ModalShell } from './CockpitBits';
import { MO_FULL } from './cockpitUi';
import type { StoredOffsets, StoredWorkload } from './kpiConfig';

const FIELD = 'w-full bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400';

const MODULES: { key: string; label: string }[] = [
    { key: 'vorank', label: 'Vorank.' },
    { key: 'sigeplan', label: 'SiGe-Plan' },
    { key: 'asplan', label: 'A+S-Plan' },
    { key: 'einweisung', label: 'Einweisung' },
    { key: 'unterlage', label: 'Unterlage' },
    { key: 'site_rules', label: 'Bauordnung' },
    { key: 'planning_general', label: 'Planung' },
];

export interface CockpitSettingsProps {
    year: number;
    offsets: StoredOffsets;
    onOffsets: (next: StoredOffsets) => void;
    workload: StoredWorkload;
    onWorkload: (next: StoredWorkload) => void;
    lastEdit: { name: string; date: string } | null;
    saving: boolean;
    /**
     * Grund, warum gerade nicht gespeichert werden darf — null heisst „frei".
     * Solange die Konfiguration des gewaehlten Jahres nicht geladen ist, stuenden
     * hier die Werte eines anderen Jahres bzw. blosse Vorgabewerte; ein Speichern
     * wuerde die echte Zeile ueberschreiben (Befunde G2 und G3).
     */
    saveBlocked: string | null;
    onSave: () => void;
    onClose: () => void;
}

export const CockpitSettings: React.FC<CockpitSettingsProps> = ({
    year, offsets, onOffsets, workload, onWorkload, lastEdit, saving, saveBlocked, onSave, onClose,
}) => {
    const [tab, setTab] = useState<'offsets' | 'workload'>('offsets');
    // Solange die Konfiguration nicht gespeichert werden darf, muessen auch die
    // Felder gesperrt sein — sonst wirkt eine Eingabe erst gueltig, geht beim
    // naechsten Laden aber wieder verloren (Befund I2).
    const disabled = !!saveBlocked;

    const setRange = (index: number, field: 'min' | 'max' | 'minutes', value: number) => {
        const ranges = workload.price_ranges.map((r, i) => (i === index ? { ...r, [field]: value } : r));
        onWorkload({ ...workload, price_ranges: ranges });
    };

    // Rohtext je Eingabefeld, solange der Nutzer tippt (Befund I1): ein
    // kontrolliertes `type="number"`-Feld, dessen Wert bei jedem Tastendruck aus
    // dem gespeicherten Wert neu berechnet wird, kann kein Komma/Dezimalpunkt am
    // Ende halten ("1200." wird sofort wieder zu "1200", die naechste Ziffer
    // ergibt "12005"). Erst beim Verlassen des Feldes (oder Enter) wird der Text
    // geparst und uebernommen; bei ungueltiger Eingabe bleibt der zuletzt
    // gespeicherte Wert stehen.
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    // Preis-Zeit-Zeile, deren Min/Max gerade min > max ergeben wuerde: nicht
    // uebernehmen, aber auch nicht still verwerfen — das Feld bleibt rot
    // gerahmt, bis die Eingabe korrigiert wird.
    const [invalidRanges, setInvalidRanges] = useState<Record<number, boolean>>({});

    const draftValue = (key: string, stored: string): string => drafts[key] ?? stored;
    const setDraft = (key: string, value: string) => setDrafts((prev) => ({ ...prev, [key]: value }));
    const clearDraft = (key: string) => setDrafts((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
    });
    const commitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') e.currentTarget.blur();
    };

    const commitOffset = (i: number) => {
        const key = `offset-${i}`;
        const raw = drafts[key];
        if (raw === undefined) return;
        const euro = parseFloat(raw.replace(',', '.'));
        if (Number.isFinite(euro)) {
            const next = { ...offsets };
            if (euro === 0) delete next[String(i)]; else next[String(i)] = euro;
            onOffsets(next);
        }
        // Ungueltige Eingabe: der zuletzt gespeicherte Wert bleibt stehen.
        clearDraft(key);
    };

    const commitRange = (i: number, field: 'min' | 'max' | 'minutes') => {
        const key = `range-${i}-${field}`;
        const raw = drafts[key];
        if (raw === undefined) return;
        const parsed = parseInt(raw, 10);
        if (!Number.isFinite(parsed)) { clearDraft(key); return; }
        if (field === 'minutes') {
            setRange(i, 'minutes', parsed);
            clearDraft(key);
            return;
        }
        const range = workload.price_ranges[i];
        const nextMin = field === 'min' ? parsed : range.min;
        const nextMax = field === 'max' ? parsed : range.max;
        if (nextMin > nextMax) {
            setInvalidRanges((prev) => ({ ...prev, [i]: true }));
            return;
        }
        setInvalidRanges((prev) => {
            if (!(i in prev)) return prev;
            const next = { ...prev };
            delete next[i];
            return next;
        });
        setRange(i, field, parsed);
        clearDraft(key);
    };

    const commitMinutes = (key: string, moduleKey: string) => {
        const raw = drafts[key];
        if (raw === undefined) return;
        const parsed = parseInt(raw, 10);
        if (Number.isFinite(parsed)) {
            onWorkload({ ...workload, assignment_minutes: { ...workload.assignment_minutes, [moduleKey]: parsed } });
        }
        clearDraft(key);
    };

    return (
        <ModalShell
            title={`Cockpit-Einstellungen ${year}`}
            width="max-w-4xl"
            onClose={onClose}
            subtitle={
                <div className="flex bg-white/10 p-0.5 rounded-lg">
                    <button onClick={() => setTab('offsets')} className={`px-4 py-1 rounded text-[11px] font-semibold ${tab === 'offsets' ? 'bg-white text-slate-800' : 'text-slate-300'}`}>
                        Externer Umsatz
                    </button>
                    <button onClick={() => setTab('workload')} className={`px-4 py-1 rounded text-[11px] font-semibold ${tab === 'workload' ? 'bg-white text-slate-800' : 'text-slate-300'}`}>
                        Auslastung
                    </button>
                </div>
            }
            footer={
                <>
                    {lastEdit
                        ? <span className="text-[11px] text-slate-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />{lastEdit.name}, {new Date(lastEdit.date).toLocaleString('de-DE')}
                        </span>
                        : <span />}
                    <div className="flex items-center gap-3">
                        {saveBlocked && <span className="text-[11px] text-amber-600">{saveBlocked}</span>}
                        {saving && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                        <Button variant="primary" size="sm" onClick={onSave} disabled={saving || !!saveBlocked} icon={<Save className="w-3.5 h-3.5" />}>
                            Speichern
                        </Button>
                    </div>
                </>
            }
        >
            {tab === 'offsets' ? (
                <div className="space-y-3">
                    <p className="text-[11px] text-slate-600 bg-blue-50 border border-blue-100 p-3 rounded-xl">
                        Extern erfasster Umsatz (z. B. aus einem Altsystem), der in die erbrachten Leistungen des
                        Jahres {year} einfließen soll. Pro Monat in Euro. Wirkt nur ohne Mitarbeiterfilter.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {MO_FULL.map((name, i) => (
                            <div key={name} className="p-3 rounded-xl border border-slate-200 focus-within:border-blue-400">
                                <label className="text-[11px] font-semibold text-slate-400 uppercase block mb-1" htmlFor={`offset-${i}`}>{name}</label>
                                <input
                                    id={`offset-${i}`}
                                    type="text"
                                    inputMode="decimal"
                                    value={draftValue(`offset-${i}`, offsets[String(i)] !== undefined ? String(offsets[String(i)]) : '')}
                                    onChange={(e) => setDraft(`offset-${i}`, e.target.value)}
                                    onBlur={() => commitOffset(i)}
                                    onKeyDown={commitOnEnter}
                                    disabled={disabled}
                                    className="w-full bg-transparent font-semibold text-slate-800 outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="0"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    <section>
                        <h4 className="text-xs font-semibold text-slate-700 uppercase mb-1">Preis-Zeit-Staffelung</h4>
                        <p className="text-[11px] text-slate-500 mb-3">
                            Minuten je Begehung nach Positionspreis. Der Forecast rechnet mit der Staffel des
                            mittleren Begehungspreises im Bestand.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {workload.price_ranges.map((r, i) => (
                                <div key={i} className="p-3 rounded-xl border border-slate-200 flex items-center gap-2">
                                    <input
                                        type="number"
                                        value={draftValue(`range-${i}-min`, String(r.min))}
                                        onChange={(e) => setDraft(`range-${i}-min`, e.target.value)}
                                        onBlur={() => commitRange(i, 'min')}
                                        onKeyDown={commitOnEnter}
                                        disabled={disabled}
                                        className={`${FIELD} disabled:opacity-50 disabled:cursor-not-allowed ${invalidRanges[i] ? 'border-red-400 focus:border-red-400' : ''}`}
                                    />
                                    <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />
                                    <input
                                        type="number"
                                        value={draftValue(`range-${i}-max`, String(r.max))}
                                        onChange={(e) => setDraft(`range-${i}-max`, e.target.value)}
                                        onBlur={() => commitRange(i, 'max')}
                                        onKeyDown={commitOnEnter}
                                        disabled={disabled}
                                        className={`${FIELD} disabled:opacity-50 disabled:cursor-not-allowed ${invalidRanges[i] ? 'border-red-400 focus:border-red-400' : ''}`}
                                    />
                                    <input
                                        type="number"
                                        value={draftValue(`range-${i}-minutes`, String(r.minutes))}
                                        onChange={(e) => setDraft(`range-${i}-minutes`, e.target.value)}
                                        onBlur={() => commitRange(i, 'minutes')}
                                        onKeyDown={commitOnEnter}
                                        disabled={disabled}
                                        className="w-16 bg-blue-600 text-white rounded-lg px-2 py-1.5 text-xs font-semibold text-center outline-none shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Minuten"
                                    />
                                </div>
                            ))}
                        </div>
                    </section>
                    <section>
                        <h4 className="text-xs font-semibold text-slate-700 uppercase mb-3">Modul-Fixzeiten (Minuten je Stück)</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                            {MODULES.map((m) => (
                                <div key={m.key} className="p-3 rounded-xl border border-slate-200">
                                    <label className="text-[11px] font-semibold text-slate-400 uppercase block mb-1" htmlFor={`min-${m.key}`}>{m.label}</label>
                                    <input
                                        id={`min-${m.key}`}
                                        type="number"
                                        value={draftValue(`min-${m.key}`, String(workload.assignment_minutes[m.key] ?? 0))}
                                        onChange={(e) => setDraft(`min-${m.key}`, e.target.value)}
                                        onBlur={() => commitMinutes(`min-${m.key}`, m.key)}
                                        onKeyDown={commitOnEnter}
                                        disabled={disabled}
                                        className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-semibold text-indigo-600 text-center outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            )}
        </ModalShell>
    );
};
